/* eslint-disable react-refresh/only-export-components */
import {
  FC,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { WithChildren } from "../helpers";
import { DrawerComponent } from "../assets/ts/components";

const I18N_CONFIG_KEY = "i18nConfig";

export type Lang = "en" | "ar";
export type Direction = "ltr" | "rtl";

type I18nConfig = {
  selectedLang: Lang;
};

type I18nContextValue = I18nConfig & {
  direction: Direction;
};

const initialState: I18nConfig = {
  selectedLang: "ar",
};

let languageChangeHandler: ((lang: Lang) => void) | undefined;

function getConfig(): I18nConfig {
  const ls = localStorage.getItem(I18N_CONFIG_KEY);
  if (ls) {
    try {
      return JSON.parse(ls) as I18nConfig;
    } catch (er) {
      console.error(er);
    }
  }
  return initialState;
}

export function langToDirection(lang: Lang): Direction {
  return lang === "ar" ? "rtl" : "ltr";
}

export function getDirection(): Direction {
  return (localStorage.getItem("direction") as Direction) || "rtl";
}

function syncDirectionStorage(lang: Lang) {
  const direction = langToDirection(lang);
  localStorage.setItem(I18N_CONFIG_KEY, JSON.stringify({ selectedLang: lang }));
  localStorage.setItem("direction", direction);
  return direction;
}

export function setLanguage(lang: string) {
  const selectedLang = lang as Lang;
  syncDirectionStorage(selectedLang);
  languageChangeHandler?.(selectedLang);
}

function applyDocumentDirection(direction: Direction, lang: Lang) {
  document.documentElement.setAttribute("dir", direction);
  document.documentElement.setAttribute("lang", lang);
  document.body.setAttribute("dir", direction);
  document.body.style.direction = direction;
}

const I18nContext = createContext<I18nContextValue>({
  ...initialState,
  direction: "rtl",
});

const useLang = (): Lang => {
  return useContext(I18nContext).selectedLang;
};

const useDirection = (): Direction => {
  return useContext(I18nContext).direction;
};

const StreamI18nProvider: FC<WithChildren> = ({ children }) => {
  const [config, setConfig] = useState<I18nConfig>(getConfig);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const direction = langToDirection(config.selectedLang);

  useEffect(() => {
    languageChangeHandler = (lang) => {
      setIsTransitioning(true);
      setConfig({ selectedLang: lang });
    };
    return () => {
      languageChangeHandler = undefined;
    };
  }, []);

  useEffect(() => {
    applyDocumentDirection(direction, config.selectedLang);
  }, [direction, config.selectedLang]);

  useEffect(() => {
    if (!isTransitioning) {
      return;
    }

    // Locale change re-renders the tree and React resets sidebar className,
    // wiping imperative `drawer` / `drawer-start` classes. Restore after paint
    // (same as window resize → DrawerComponent.update).
    const timer = window.setTimeout(() => {
      DrawerComponent.hideAll();
      DrawerComponent.updateAll();
      setIsTransitioning(false);
    }, 70);

    return () => window.clearTimeout(timer);
  }, [isTransitioning, direction]);

  const value = useMemo<I18nContextValue>(
    () => ({
      ...config,
      direction,
    }),
    [config, direction]
  );

  return (
    <I18nContext.Provider value={value}>
      <div
        className={direction}
        style={{
          minHeight: "inherit",
          opacity: isTransitioning ? 0.92 : 1,
          transition: "opacity 150ms ease-in-out",
        }}
      >
        {children}
      </div>
    </I18nContext.Provider>
  );
};

export { StreamI18nProvider, useLang, useDirection };
